import { CustomPopover, Iconify, usePopover } from '@btsis/ui';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { notificationEventKey, notificationRecordPath, relativeTime } from './notification-labels';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMyNotifications,
} from './queries';

/**
 * The in-app centre: the `in_app` rows addressed to the signed-in person, and
 * nobody else's. It is polled rather than pushed — §7.1 buys a minute of
 * latency instead of a socket — and it carries no permission, because the
 * caller is the whole authorization. The link to the delivery list is offered
 * only behind the permission that route checks.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const popover = usePopover();
  const centre = useMyNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const items = centre.data?.items ?? [];
  const unread = centre.data?.unread ?? 0;

  return (
    <>
      <Tooltip title={t('notification.centre.title')}>
        <IconButton
          aria-label={t('notification.centre.title')}
          data-action="notification-centre"
          color={popover.open ? 'primary' : 'default'}
          onClick={popover.onOpen}
        >
          <Badge badgeContent={unread} color="error" max={99}>
            <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
          </Badge>
        </IconButton>
      </Tooltip>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        arrowPlacement="top-right"
      >
        <Box sx={{ width: 380, maxWidth: '100%' }}>
          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5, alignItems: 'center' }}>
            <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1">{t('notification.centre.title')}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('notification.centre.unread', { count: unread })}
              </Typography>
            </Stack>
            {unread > 0 && (
              <Tooltip title={t('notification.centre.markAllRead')}>
                <IconButton
                  aria-label={t('notification.centre.markAllRead')}
                  data-action="notification-mark-all-read"
                  disabled={markAllRead.isPending}
                  onClick={() => markAllRead.mutate()}
                >
                  <Iconify icon="solar:check-read-line-duotone" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Divider sx={{ borderStyle: 'dashed' }} />

          {items.length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('notification.centre.empty')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {items.map((row) => (
                <Stack
                  key={row.id}
                  direction="row"
                  spacing={1}
                  sx={{
                    px: 2,
                    py: 1.5,
                    alignItems: 'flex-start',
                    bgcolor: row.readAt ? 'transparent' : 'action.hover',
                  }}
                >
                  <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                      {row.subject ??
                        t(`notification.events.${notificationEventKey(row.event)}`, {
                          defaultValue: row.event,
                        })}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}
                    >
                      {row.body === '' ? t('notification.hiddenWhileMasked') : row.body}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      {relativeTime(row.queuedAt)}
                    </Typography>
                    {notificationRecordPath(row.causeEntity, row.causeId) !== null && (
                      <Button
                        size="small"
                        color="inherit"
                        data-action="notification-open-record"
                        sx={{ alignSelf: 'flex-start', px: 0.5 }}
                        onClick={() => {
                          const path = notificationRecordPath(row.causeEntity, row.causeId);
                          if (path === null) return;
                          popover.onClose();
                          void navigate(path);
                        }}
                      >
                        {t('notification.centre.openRecord')}
                      </Button>
                    )}
                  </Stack>
                  {!row.readAt && (
                    <Tooltip title={t('notification.centre.markRead')}>
                      <IconButton
                        size="small"
                        aria-label={t('notification.centre.markRead')}
                        data-action="notification-mark-read"
                        disabled={markRead.isPending}
                        onClick={() => markRead.mutate({ id: row.id })}
                      >
                        <Iconify icon="solar:check-circle-bold" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Box>
          )}

          {can('notification.view') && (
            <>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <Box sx={{ p: 1 }}>
                <Button
                  fullWidth
                  color="inherit"
                  data-action="notification-view-all"
                  onClick={() => {
                    popover.onClose();
                    void navigate('/notifications');
                  }}
                >
                  {t('notification.centre.viewAll')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </CustomPopover>
    </>
  );
}
